import React from "react";
import { createPortal } from "react-dom";
import Autosuggest from "react-autosuggest";

function Autocomplete({ value, onChange, onSuggestionsFetch }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [inputRect, setInputRect] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const debounceTimer = setTimeout(async () => {
      if (value) {
        const result = await onSuggestionsFetch(value);
        setSuggestions(result);
      } else {
        setSuggestions([]);
      }
    }, 800);

    return () => clearTimeout(debounceTimer);
  }, [value, onSuggestionsFetch]);

  React.useEffect(() => {
    if (suggestions.length > 0 && inputRef.current) {
      const updateRect = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setInputRect(rect);
        }
      };

      updateRect();
      window.addEventListener("scroll", updateRect, true);
      window.addEventListener("resize", updateRect);

      return () => {
        window.removeEventListener("scroll", updateRect, true);
        window.removeEventListener("resize", updateRect);
      };
    }
  }, [suggestions]);

  const theme = {
    container: {
      position: "relative",
      display: "inline-block",
      width: "100%",
    },
    input: {
      width: "100%",
      padding: "0.625rem 0.75rem",
      fontSize: "0.875rem",
      border: "1px solid #c0cddf",
      borderRadius: "0.25rem",
      outline: "none",
      transition: "border-color 0.2s ease",
      borderColor: "#c0cddf",
    },
    inputFocused: {
      borderColor: "#007bff",
    },
    suggestionsContainer: {
      position: "fixed",
      top: inputRect ? `${inputRect.bottom + window.scrollY}px` : 0,
      left: inputRect ? `${inputRect.left + window.scrollX}px` : 0,
      width: inputRect ? `${inputRect.width}px` : "100%",
      zIndex: 9999,
      backgroundColor: "white",
      border: "0",
      borderTop: "none",
      borderRadius: "0 0 0.25rem 0.25rem",
      maxHeight: "12.5rem",
      overflowY: "auto",
      boxShadow: "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyle: "none",
    },
    suggestion: {
      padding: "0.4rem 0.5rem",
      cursor: "pointer",
    },
    suggestionHighlighted: {
      backgroundColor: "#ecf4fe",
    },
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    if (!inputRect || suggestions.length === 0) return null;

    return createPortal(
      <div {...containerProps} style={theme.suggestionsContainer}>
        {children}
      </div>,
      document.body
    );
  };

  return (
    <Autosuggest
      suggestions={suggestions}
      onSuggestionsFetchRequested={({ value = "" }) =>
        onSuggestionsFetch(value)
      }
      onSuggestionsClearRequested={() => onSuggestionsFetch("")}
      getSuggestionValue={s => s.label}
      renderSuggestion={s => <div style={theme.suggestion}>{s.label}</div>}
      renderSuggestionsContainer={renderSuggestionsContainer}
      theme={theme}
      inputProps={{
        placeholder: "Type to search...",
        value,
        onChange: (_, { newValue = "" }) => onChange(newValue, suggestions),
        ref: inputRef,
      }}
    />
  );
}

export default Autocomplete;
